import React, { useState } from "react";
import { Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, ListItemIcon, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import {
  MoreHorizontal, MessageSquare, Paperclip, Link2, Flag, OctagonAlert, CalendarClock, ExternalLink, UserCheck, User, ArrowRightLeft, Gauge, Tag, Timer,
  Clock, Eye, Copy, GitBranch, Archive, Trash2, MessageSquarePlus, Hash, CalendarDays, Play,
} from "lucide-react";
import { GlassCard, T, TYPE, SERIES, formatDate } from "../../user/complaints/shared";
import { usePm } from "../PmContext";
import { TypeIcon, PriorityIcon, Assignee, Labels, minutesText } from "../ui";
import { PersonPicker, fieldSx } from "../inputs";

const hash = (s) => {
  let h = 0;
  for (let i = 0; i < String(s).length; i += 1) h = String(s).charCodeAt(i) + ((h << 5) - h);
  return SERIES[Math.abs(h) % SERIES.length];
};

/** Left colour stripe — one restrained accent, never a full-colour card. */
const stripeColor = (issue, colorBy, ctx) => {
  switch (colorBy) {
    case "priority": return ctx.priorityById.get(String(issue.priorityId))?.color;
    case "type": return ctx.typeById.get(String(issue.typeId))?.color;
    case "assignee": return issue.assignee?.id ? hash(issue.assignee.id) : T.border;
    case "label": return issue.labels?.[0] ? hash(issue.labels[0]) : T.border;
    case "due": {
      if (!issue.dueDate || issue.statusCategory === "done") return T.border;
      const d = new Date(issue.dueDate) - Date.now();
      return d < 0 ? "#F04438" : d < 3 * 86400000 ? "#F79009" : T.border;
    }
    default: return null;
  }
};

/** Card on the board. `fields` = board.cardFields; `compact` shows key + summary only. */
export const BoardCard = ({ issue, board, epics, components, sprints, compact, selected, selecting, onSelect, onOpen, onMenu, onHover, draggable, onDragStart, overWip }) => {
  const ctx = usePm();
  const fields = new Set(board.cardFields || []);
  const epic = issue.epicId && epics?.find((e) => String(e._id) === String(issue.epicId));
  const overdue = issue.dueDate && issue.statusCategory !== "done" && new Date(issue.dueDate) < new Date();
  const stripe = stripeColor(issue, board.cardLayout?.colorBy, ctx);
  const blocked = issue.blockedBy?.length > 0;
  const unit = board.estimationConfig?.unit || "storyPoints";
  return (
    <GlassCard
      role="button" tabIndex={0} aria-label={`${issue.key} ${issue.summary}`}
      draggable={draggable} onDragStart={onDragStart} onMouseEnter={() => onHover?.(issue)} onFocus={() => onHover?.(issue)}
      onClick={(e) => (selecting || e.shiftKey || e.metaKey || e.ctrlKey ? onSelect(!selected) : onOpen(issue))}
      onContextMenu={(e) => { e.preventDefault(); onMenu(e.currentTarget, issue); }}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(issue); }}
      sx={{
        p: compact ? 1 : 1.25,
        mb: 1,
        position: "relative",
        cursor: draggable ? "grab" : "pointer",
        borderRadius: "8px",
        boxShadow: "0 1px 2px rgba(9, 30, 66, 0.2), 0 0 1px rgba(9, 30, 66, 0.25)",
        border: `1px solid ${selected ? T.brand : blocked ? "#FDA29B" : "#DFE1E6"}`,
        bgcolor: selected ? T.brandSoft : issue.flagged ? "#FFFAEB" : "#fff",
        borderLeft: stripe ? `3px solid ${stripe}` : undefined,
        outline: overWip ? "1px dashed #F04438" : "none",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          boxShadow: "0 4px 8px -2px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)",
          borderColor: selected ? T.brand : "#C1C7D0",
          "& .card-menu": { opacity: 1 },
        },
        "&:focus-visible": { outline: `2px solid ${T.brand}` },
      }}>
      <Stack direction="row" alignItems="flex-start" spacing={0.5}>
        {selecting && <Checkbox size="small" checked={selected} sx={{ p: 0, mr: 0.5 }} onClick={(e) => e.stopPropagation()} onChange={(e) => onSelect(e.target.checked)} />}
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, lineHeight: 1.45, color: "#172B4D", mb: compact ? 0.25 : 1, wordBreak: "break-word", overflowWrap: "anywhere", flexGrow: 1 }}>{issue.summary}</Typography>
        <IconButton className="card-menu" size="small" aria-label="Quick actions" sx={{ opacity: { xs: 1, md: 0 }, p: 0.25, mt: -0.25 }} onClick={(e) => { e.stopPropagation(); onMenu(e.currentTarget, issue); }}><MoreHorizontal size={15} /></IconButton>
      </Stack>
      {!compact && (
        <>
          {(blocked || issue.flagged) && fields.has("blocked") !== false && (
            <Stack spacing={0.4} sx={{ mb: 0.75 }}>
              {blocked && fields.has("blocked") && (
                <Tooltip title={`Blocked by ${issue.blockedBy.map((b) => `${b.key} ${b.summary}`).join(", ")}`}>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "#B42318" }}><OctagonAlert size={12} /><Typography sx={{ fontSize: "0.68rem", fontWeight: 700 }}>Blocked by {issue.blockedBy.map((b) => b.key).join(", ")}</Typography></Stack>
                </Tooltip>
              )}
              {issue.flagged && fields.has("flagged") && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "#B54708" }}><Flag size={12} /><Typography sx={{ fontSize: "0.68rem", fontWeight: 700 }} noWrap>Flagged{issue.flagReason ? `: ${issue.flagReason}` : ""}</Typography></Stack>
              )}
            </Stack>
          )}
          {epic && fields.has("epic") && <Chip size="small" label={epic.summary} sx={{ height: 18, fontSize: "0.64rem", mb: 0.75, maxWidth: "100%", bgcolor: "#F4EBFF", color: "#6941C6", fontWeight: 600 }} />}
          {fields.has("labels") && issue.labels?.length > 0 && <Box sx={{ mb: 0.75 }}><Labels labels={issue.labels} max={3} /></Box>}
          {fields.has("components") && issue.componentIds?.length > 0 && <Typography sx={{ fontSize: "0.66rem", color: T.inkSecondary, mb: 0.5 }} noWrap>{issue.componentIds.map((id) => components?.get(String(id))?.name).filter(Boolean).join(", ")}</Typography>}
          {fields.has("sprint") && issue.sprintId && sprints?.get(String(issue.sprintId)) && <Typography sx={{ fontSize: "0.66rem", color: T.inkSecondary, mb: 0.5 }}>{sprints.get(String(issue.sprintId)).name}</Typography>}
          {issue.cardFields && Object.entries(issue.cardFields).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
            <Typography key={k} sx={{ fontSize: "0.66rem", color: T.inkSecondary }} noWrap>{ctx.customFields.find((f) => f.key === k)?.label || k}: {Array.isArray(v) ? v.join(", ") : String(v)}</Typography>
          ))}
          {fields.has("subtasks") && issue.subtasks?.total > 0 && (
            <Tooltip title={`${issue.subtasks.done} of ${issue.subtasks.total} sub-tasks done`}>
              <Box sx={{ mb: 1, mt: 0.5 }}>
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: T.inkMuted, mb: 0.35 }}>
                  Sub-tasks {issue.subtasks.done}/{issue.subtasks.total}
                </Typography>
                <Box sx={{ height: 4, borderRadius: 2, bgcolor: "#E2E8F0", overflow: "hidden", width: "100%" }}>
                  <Box sx={{ height: "100%", width: `${Math.round((issue.subtasks.done / issue.subtasks.total) * 100)}%`, bgcolor: "#12B76A", borderRadius: 2 }} />
                </Box>
              </Box>
            </Tooltip>
          )}
        </>
      )}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1, pt: 0.25 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          {fields.has("type") && <TypeIcon typeId={issue.typeId} size={15} />}
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: issue.statusCategory === "done" ? T.inkMuted : "#42526E", textDecoration: issue.statusCategory === "done" ? "line-through" : "none", letterSpacing: "0.2px", whiteSpace: "nowrap" }}>
            {issue.key}
          </Typography>
          {!compact && fields.has("dueDate") && issue.dueDate && (
            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: overdue ? "#B42318" : T.inkMuted, ml: 0.5 }}>
              <CalendarClock size={11} />
              <Typography sx={{ fontSize: "0.66rem" }}>{formatDate(issue.dueDate)}</Typography>
            </Stack>
          )}
          {!compact && fields.has("comments") && issue.commentCount > 0 && (
            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: T.inkMuted, ml: 0.5 }}>
              <MessageSquare size={11} />
              <Typography sx={{ fontSize: "0.66rem" }}>{issue.commentCount}</Typography>
            </Stack>
          )}
          {!compact && fields.has("attachments") && issue.attachmentCount > 0 && (
            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: T.inkMuted, ml: 0.5 }}>
              <Paperclip size={11} />
              <Typography sx={{ fontSize: "0.66rem" }}>{issue.attachmentCount}</Typography>
            </Stack>
          )}
        </Stack>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
          {fields.has("storyPoints") && (unit === "storyPoints" ? issue.storyPoints !== null && issue.storyPoints !== undefined : issue.estimate > 0) && (
            <Tooltip title={unit === "storyPoints" ? "Story points" : "Estimate"}>
              <Box component="span" sx={{ minWidth: 20, height: 20, px: 0.6, borderRadius: "10px", bgcolor: "#EAECF0", fontSize: "0.7rem", fontWeight: 700, color: "#344054", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                {unit === "storyPoints" ? issue.storyPoints : issue.estimate}
              </Box>
            </Tooltip>
          )}
          {fields.has("priority") && <PriorityIcon priorityId={issue.priorityId} size={15} />}
          {fields.has("reporter") && issue.reporter?.id && (
            <Tooltip title={`Reporter: ${issue.reporter.name}`}>
              <Box component="span" sx={{ display: "inline-flex", opacity: 0.7 }}>
                <Assignee person={issue.reporter} size={18} />
              </Box>
            </Tooltip>
          )}
          {fields.has("assignee") && <Assignee person={issue.assignee} size={22} />}
        </Stack>
      </Stack>
    </GlassCard>
  );
};

// ---------------------------------------------------------------------------
// Quick actions / inline editing

/** One small input dialog for inline edits (points, due date, label, comment, work log, flag reason). */
export const QuickEdit = ({ state, onClose, onSubmit }) => {
  const [value, setValue] = useState("");
  const [extra, setExtra] = useState("");
  React.useEffect(() => {
    setValue(state?.initial ?? "");
    setExtra("");
  }, [state]);
  if (!state) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>{state.title} · {state.issue.key}</DialogTitle>
      <DialogContent dividers>
        {state.kind === "person" ? (
          <PersonPicker label="Assignee" value={value} onChange={setValue} />
        ) : (
          <TextField autoFocus fullWidth size="small" type={state.kind === "number" ? "number" : state.kind === "date" ? "date" : "text"} multiline={state.kind === "multiline"} minRows={state.kind === "multiline" ? 3 : undefined}
            label={state.label} placeholder={state.placeholder} InputLabelProps={state.kind === "date" ? { shrink: true } : undefined} value={value}
            onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && state.kind !== "multiline") onSubmit(value, extra); }} sx={fieldSx} />
        )}
        {state.extraLabel && <TextField fullWidth size="small" label={state.extraLabel} value={extra} onChange={(e) => setExtra(e.target.value)} sx={{ ...fieldSx, mt: 1.5 }} />}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={() => onSubmit(value, extra)}>Save</Button></DialogActions>
    </Dialog>
  );
};

/**
 * Context menu of a card. `act(action, issue, arg)` does the work (board canvas),
 * so every action goes through the existing issue APIs and permissions.
 */
export const CardMenu = ({ anchor, issue, board, columns, sprints, perms, onClose, act }) => {
  const ctx = usePm();
  const [sub, setSub] = useState(null);
  if (!anchor || !issue) return null;
  const can = (p) => perms.includes(p);
  const close = () => {
    setSub(null);
    onClose();
  };
  const run = (a, arg) => {
    close();
    act(a, issue, arg);
  };
  const Item = ({ icon: Icon, label, onClick, danger, disabled }) => (
    <MenuItem dense onClick={onClick} disabled={disabled} sx={danger ? { color: "#B42318" } : undefined}>
      <ListItemIcon sx={{ minWidth: 28, color: danger ? "#B42318" : T.inkSecondary }}><Icon size={15} /></ListItemIcon>
      <Typography sx={{ fontSize: TYPE.small }}>{label}</Typography>
    </MenuItem>
  );
  const column = columns.find((c) => c.statusIds.map(String).includes(String(issue.statusId)));
  const nextCol = column ? columns[columns.indexOf(column) + 1] : null;
  return (
    <>
      <Menu anchorEl={anchor} open={Boolean(anchor) && !sub} onClose={close} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <Item icon={ExternalLink} label="Open" onClick={() => run("open")} />
        <Divider />
        {can("issue.transition") && nextCol && column?.statusIds && issue.statusCategory === "todo" && <Item icon={Play} label={`Start progress (→ ${nextCol.name})`} onClick={() => run("column", nextCol)} />}
        {can("issue.transition") && <Item icon={ArrowRightLeft} label="Change status / column…" onClick={() => setSub("column")} />}
        {(can("issue.assign") || can("issue.update")) && <Item icon={UserCheck} label="Assign to me" onClick={() => run("assignMe")} />}
        {can("issue.assign") && <Item icon={User} label="Assign…" onClick={() => run("assign")} />}
        {can("issue.update") && <Item icon={Gauge} label="Priority…" onClick={() => setSub("priority")} />}
        {can("issue.update") && <Item icon={Hash} label="Story points…" onClick={() => run("points")} />}
        {can("issue.update") && <Item icon={CalendarDays} label="Due date…" onClick={() => run("due")} />}
        {can("issue.update") && <Item icon={Tag} label="Add label…" onClick={() => run("label")} />}
        {can("issue.update") && <Item icon={Flag} label={issue.flagged ? "Remove flag" : "Flag (impediment)…"} onClick={() => run("flag")} />}
        {board.type === "scrum" && can("issue.update") && <Item icon={Timer} label="Add to sprint…" onClick={() => setSub("sprint")} />}
        {board.type === "scrum" && can("issue.update") && issue.sprintId && <Item icon={Timer} label="Remove from sprint" onClick={() => run("sprint", null)} />}
        <Divider />
        {can("issue.comment") && <Item icon={MessageSquarePlus} label="Add comment…" onClick={() => run("comment")} />}
        {can("issue.worklog") && <Item icon={Clock} label="Log work…" onClick={() => run("worklog")} />}
        <Item icon={Eye} label="Watch" onClick={() => run("watch")} />
        {can("issue.link") && <Item icon={Link2} label="Link issue…" onClick={() => run("open", "link")} />}
        {can("issue.create") && issue.typeLevel !== 2 && <Item icon={GitBranch} label="Create sub-task…" onClick={() => run("subtask")} />}
        {can("issue.clone") && can("issue.create") && <Item icon={Copy} label="Clone" onClick={() => run("clone")} />}
        {can("issue.move") && <Item icon={ArrowRightLeft} label="Move to project…" onClick={() => run("open", "move")} />}
        <Divider />
        {can("issue.update") && <Item icon={Archive} label="Archive" onClick={() => run("archive")} />}
        {can("issue.delete") && <Item icon={Trash2} label="Delete" danger onClick={() => run("delete")} />}
      </Menu>
      <Menu anchorEl={anchor} open={sub === "column"} onClose={close}>
        {columns.map((c) => <MenuItem dense key={c._id} disabled={c.statusIds.map(String).includes(String(issue.statusId))} onClick={() => run("column", c)}><Typography sx={{ fontSize: TYPE.small }}>{c.name}</Typography></MenuItem>)}
      </Menu>
      <Menu anchorEl={anchor} open={sub === "priority"} onClose={close}>
        {ctx.priorities.filter((p) => p.isActive).map((p) => <MenuItem dense key={p._id} selected={String(p._id) === String(issue.priorityId)} onClick={() => run("priority", p._id)}><PriorityIcon priorityId={p._id} withLabel /></MenuItem>)}
      </Menu>
      <Menu anchorEl={anchor} open={sub === "sprint"} onClose={close}>
        {!sprints.length && <MenuItem dense disabled>No open sprints</MenuItem>}
        {sprints.map((s) => <MenuItem dense key={s._id} selected={String(s._id) === String(issue.sprintId)} onClick={() => run("sprint", s._id)}><Typography sx={{ fontSize: TYPE.small }}>{s.name}{s.status === "active" ? " (active)" : ""}</Typography></MenuItem>)}
      </Menu>
    </>
  );
};
